import { createScenePreviews, ScenePreview } from './player/scenePreviews';
import { Scene } from 'types/Scene';
import { isNil, last } from 'ramda';
import { Effects } from 'types/Effects';
import { SceneVolume } from 'types/sceneVolume';
import { Audio } from 'types/Audio';
import { createAudioPLayer, AudioPlayer } from './player/audioPlayer';
import { WATERMARK_DIMENSION, WATERMARK_OFFSET, WATERMARK_SRC } from 'constants/watermark';
import VideoScenePreview from './player/scenePreviews/VideoScenePreview';

export class Player {
  canvas: HTMLCanvasElement;

  private ctx: CanvasRenderingContext2D;

  private rafId = -1;

  private lastFrameTime = 0;

  public playing = false;

  public time = 0;

  private duration = 0;

  private scenes: ScenePreview[] = [];

  private audio: AudioPlayer;

  private effects: Effects;

  private volume: SceneVolume;

  private isLoading = false;

  private isWatermarkEnabled: boolean;

  private watermarkPattern: CanvasPattern | null = null;

  constructor(private setCurrentTime: (time: number) => void, private onEnd: () => void) {
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d');
    this.render();
  }

  setWatermarkEnabled = (state: boolean) => {
    if (state && isNil(this.watermarkPattern)) {
      const watermarkImage = new Image();
      watermarkImage.onload = () => {
        const watermarkImageCnv = document.createElement('canvas');
        const watermarkImageCtx = watermarkImageCnv.getContext('2d');
        watermarkImageCnv.width = WATERMARK_DIMENSION;
        watermarkImageCnv.height = WATERMARK_DIMENSION;
        watermarkImageCtx.drawImage(watermarkImage, 0, 0, WATERMARK_DIMENSION, WATERMARK_DIMENSION);
        this.watermarkPattern = this.ctx.createPattern(watermarkImageCnv, 'repeat');
      };
      watermarkImage.src = WATERMARK_SRC;
    }

    this.isWatermarkEnabled = state;
  };

  setTime = (time: number) => {
    this.time = time;
    this.updateScenesTime();
    this.setCurrentTime(time);
  };

  updateCounter = { scenes: 0, audio: 0 };

  setScenes = async (scenes: Scene[]) => {
    this.isLoading = true;
    this.pause();
    this.onEnd();

    this.updateCounter.scenes++;
    const updateNumber = this.updateCounter.scenes;

    let scenePreviews;
    try {
      scenePreviews = await createScenePreviews(scenes);
      // eslint-disable-next-line no-empty
    } catch (_) {}

    if (updateNumber !== this.updateCounter.scenes) return;

    this.scenes = scenePreviews;
    this.updateVolume();

    const lastScene = last(scenes);
    this.duration = lastScene.duration + lastScene.start;

    this.isLoading = false;

    this.updateScenesTime();
  };

  setAudio = async ({ url, duration, trimDuration, trimStart = 0 }: Audio, isPlaying: boolean) => {
    this.deleteAudio();
    this.updateCounter.audio++;
    const updateNumber = this.updateCounter.audio;

    const audioPlayer = await createAudioPLayer(
      url,
      trimDuration || duration,
      trimStart,
      isPlaying
    );
    if (updateNumber !== this.updateCounter.audio) return;
    this.audio = audioPlayer;
  };

  deleteAudio = () => {
    this.updateCounter.audio++;
    this.audio?.pause();
    this.audio = null;
  };

  setEffects = (effects: Effects) => {
    this.effects = effects;
  };

  setSize = (width: number, height: number) => {
    this.canvas.width = width;
    this.canvas.height = height;
  };

  updateVolume = () => {
    this.scenes.forEach(
      item =>
        this.volume[item.settings.id] && item.updateVolume(this.volume[item.settings.id].volume)
    );
  };

  setVolume = (volume: SceneVolume) => {
    this.volume = volume;
    this.updateVolume();
  };

  play = () => {
    if (!this.playing && this.effects) {
      if (this.time >= this.duration) {
        this.setTime(0);
      }

      this.scenes.forEach(item =>
        item.updateSettings({ isPlaying: true, effects: this.effects[item.settings.id] })
      );
      this.audio?.play();
      this.playing = true;
    }
  };

  pause = () => {
    if (this.playing) {
      this.scenes.forEach(item => item.updateSettings({ isPlaying: false, effects: null }));
      this.audio?.pause();
      this.playing = false;
    }
  };

  enableSound = () => {
    this.scenes.forEach(item => {
      item.enableSound();
    });

    this.audio?.enableSound();
  };

  destruct = () => {
    this.pause();
    window.cancelAnimationFrame(this.rafId);
  };

  waitForVideoSeeking = () => {
    this.scenes.forEach(item => {
      if (item instanceof VideoScenePreview) item.isVideoSeeking = true;
    });
  };

  private updateScenesTime() {
    this.scenes.forEach(item => item.setTime(this.time));
    this.audio?.setTime(this.time);
  }

  private render() {
    const { ctx, scenes, playing, duration, isLoading } = this;
    const { width, height } = ctx.canvas;

    this.rafId = window.requestAnimationFrame(time => {
      if (
        !isLoading &&
        !scenes.some(scene => scene instanceof VideoScenePreview && scene.isVideoSeeking)
      ) {
        if (playing) {
          this.time += time - this.lastFrameTime;

          if (this.time >= duration) {
            this.time = 0;
            this.pause();
            this.onEnd();
          }

          this.setCurrentTime(this.time);
          this.updateScenesTime();
        }

        this.lastFrameTime = time;

        ctx.clearRect(0, 0, width, height);
        scenes.forEach(item => item.render(ctx));

        if (!isLoading && this.isWatermarkEnabled && !isNil(this.watermarkPattern)) {
          this.ctx.fillStyle = this.watermarkPattern;
          this.ctx.translate(-WATERMARK_OFFSET, -WATERMARK_OFFSET);
          this.ctx.fillRect(0, 0, width + WATERMARK_OFFSET, height + WATERMARK_OFFSET);
          this.ctx.translate(WATERMARK_OFFSET, WATERMARK_OFFSET);
        }
      }

      this.render();
    });
  }
}
